import { useCallback, useEffect, useState } from "react";
import { Loader2, Minus, Plus } from "lucide-react";
import { toast } from "sonner";
import { api, formatErr, fmtDate } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";

const fmt = (n) => (n || 0).toLocaleString("de-DE");

function CustomAdjust({ u, onDone }) {
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const n = parseInt(amount, 10);
  const valid = n > 0 && n <= 100000 && reason.trim().length > 0;
  const send = async (sign) => {
    if (sign < 0 && n > (u.points || 0)) { toast.error(`${u.name} hat nur ${fmt(u.points)} Punkte`); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/users/${u.user_id}/points`, { amount: sign * n, reason: reason.trim() });
      toast.success(sign > 0 ? `${fmt(n)} Punkte gutgeschrieben` : `${fmt(n)} Punkte abgezogen`);
      setAmount(""); setReason(""); onDone(data.points);
    } catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); }
  };
  return (
    <div className="rounded-2xl bg-rose-50/60 border border-rose-100 p-4 space-y-3" data-testid="points-custom">
      <p className="text-sm font-bold">Eigener Betrag</p>
      <div className="flex gap-2">
        <Input type="number" inputMode="numeric" min={1} max={100000} value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="Punkte" className="w-28 rounded-full bg-white" data-testid="points-custom-amount" />
        <Input value={reason} onChange={(e) => setReason(e.target.value)} maxLength={120} placeholder="Grund, z. B. Gewinnspiel Juni" className="flex-1 rounded-full bg-white" data-testid="points-custom-reason" />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <button onClick={() => send(-1)} disabled={!valid || busy} data-testid="points-custom-minus" className="h-11 rounded-full border-2 border-rose-300 text-rose-600 font-bold flex items-center justify-center gap-1.5 disabled:opacity-40"><Minus className="w-4 h-4" /> Abziehen</button>
        <button onClick={() => send(1)} disabled={!valid || busy} data-testid="points-custom-plus" className="h-11 rounded-full pink-gradient text-white font-bold flex items-center justify-center gap-1.5 disabled:opacity-40">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Gutschreiben</button>
      </div>
    </div>
  );
}

function History({ userId, version }) {
  const [rows, setRows] = useState(null);
  const [end, setEnd] = useState(false);
  const load = useCallback(async (before) => {
    const { data } = await api.get("/admin/points/log", { params: { user_id: userId, ...(before ? { before } : {}) } });
    setEnd(data.items.length < 100);
    setRows((r) => (before ? [...(r || []), ...data.items] : data.items));
  }, [userId]);
  useEffect(() => { load().catch(() => setRows([])); }, [load, version]);
  if (!rows) return <Loader2 className="w-5 h-5 animate-spin text-rose-400 mx-auto" />;
  if (!rows.length) return <p className="text-sm text-slate-400 text-center py-4">Noch keine Punkte-Buchungen</p>;
  return (
    <div>
      <ul className="divide-y divide-pink-50" data-testid="points-history">
        {rows.map((r) => (
          <li key={r.id} className="flex items-center gap-3 py-2.5" data-testid={`points-tx-${r.id}`}>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold truncate">{r.reason || r.kind_label}</p>
              <p className="text-xs text-slate-400">{r.kind_label} · {fmtDate(r.created_at)}</p>
            </div>
            <span className={`font-extrabold tabular-nums ${r.amount >= 0 ? "text-emerald-600" : "text-rose-600"}`}>{r.amount >= 0 ? "+" : "−"}{fmt(Math.abs(r.amount))}</span>
          </li>
        ))}
      </ul>
      {!end && <button onClick={() => load(rows[rows.length - 1].created_at)} data-testid="points-history-more" className="w-full h-10 mt-2 rounded-full border border-rose-200 text-rose-600 text-sm font-bold">Ältere laden</button>}
    </div>
  );
}

function Exclusions({ u, onChange }) {
  const set = async (key, val) => {
    try {
      const { data } = await api.put(`/admin/users/${u.user_id}/exclusions`, { [key]: val });
      onChange({ no_contest: data.contest, no_quest: data.quest, no_points: data.points, no_missions: data.missions });
      toast.success(val ? "Ausgeschlossen" : "Wieder zugelassen");
    } catch (e) { toast.error(formatErr(e)); }
  };
  const rows = [["quest", "no_quest", "Von Community-Aufgaben ausschließen"], ["missions", "no_missions", "Von Tagesmissionen ausschließen"], ["points", "no_points", "Punkte-Sammeln sperren (keine Punkte, nichts einlösen)"]];
  return (
    <div className="rounded-2xl border border-rose-100 p-4 space-y-3" data-testid="points-exclusions">
      <p className="text-sm font-bold">Ausschlüsse</p>
      {rows.map(([key, field, label]) => (
        <label key={key} className="flex items-center justify-between gap-3 text-sm">
          <span>{label}</span>
          <Switch checked={!!u[field]} onCheckedChange={(v) => set(key, v)} data-testid={`exclude-${key}-switch`} />
        </label>
      ))}
      <p className="text-xs text-slate-400">Ausgeschlossene zählen nicht, tauchen in keiner Rangliste auf, bekommen keine Punkte und sehen einen Hinweis.</p>
    </div>
  );
}

export function PointsUserSheet({ u, onOpenChange, onChange, onPatch }) {
  const [version, setVersion] = useState(0);
  return (
    <Sheet open={!!u} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="h-[85vh] rounded-t-3xl overflow-y-auto sm:max-w-lg sm:mx-auto" data-testid="points-user-sheet">
        {u && <>
          <SheetHeader className="text-left">
            <SheetTitle>{u.name}</SheetTitle>
            <SheetDescription>Aktuell <b className="text-rose-600" data-testid="points-sheet-balance">{fmt(u.points)}</b> Punkte</SheetDescription>
          </SheetHeader>
          <div className="space-y-5 mt-4">
            <CustomAdjust u={u} onDone={(p) => { onChange(u.user_id, p); setVersion((v) => v + 1); }} />
            <Exclusions u={u} onChange={(patch) => onPatch(u.user_id, patch)} />
            <div><p className="text-sm font-bold mb-1">Punkte-Verlauf</p><History userId={u.user_id} version={version} /></div>
          </div>
        </>}
      </SheetContent>
    </Sheet>
  );
}
